import { Button, Flexer, Header, Heading, Icon, Modal, ModalClose, Portal, Rate, Text, View } from '@fold-dev/core'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TbStar, TbStarFilled, TbX } from 'react-icons/tb'
import { useFile } from '../../hooks/file.hook'
import { Loading } from '../common'
import { getArrow } from '../../helpers/arrows'

export const Screenshot = (props) => {
    const { meta, attachment, onDismiss } = props
    const { rating, context, type } = meta
    const { screenshot, annotations } = attachment
    const { lines, arrows, comments, rectangles } = annotations
    const { innerHeight, innerWidth } = window
    const containerRef = useRef(null)
    const canvasRef = useRef(null)
    const [height, setHeight] = useState<any>('100%')
    const [width, setWidth] = useState<any>('100%')
    const [coef, setCoef] = useState(0)
    const [loading, setLoading] = useState(false)
    const [backgroundImage, setBackgroundImage] = useState(screenshot)

    useEffect(() => {
        if (!canvasRef) return
        if (!canvasRef.current) return

        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')

        //canvas.width = width
        //canvas.height = height

        ctx.beginPath()

        // background
        ctx.moveTo(0, 0)
        ctx.lineTo(width, 0)
        ctx.lineTo(width, height)
        ctx.lineTo(0, height)
        ctx.lineTo(0, 0)
        ctx.closePath()

        rectangles.map((rectangle) => {
            const x = rectangle.x * coef
            const y = rectangle.y * coef
            const width = rectangle.width * coef
            const height = rectangle.height * coef

            ctx.moveTo(x, y)
            ctx.lineTo(x, y + height)
            ctx.lineTo(x + width, y + height)
            ctx.lineTo(x + width, y)
            ctx.lineTo(x, y)
            ctx.closePath()
        })

        ctx.fillStyle = 'rgba(0,0,0,0.5)'
        ctx.strokeStyle = 'rgba(0,0,0,0)'

        ctx.fill()
        ctx.stroke()
    }, [canvasRef, rectangles, width, height, coef])

    useEffect(() => {
        if (!containerRef) return
        if (!containerRef.current) return

        const dimensions = containerRef.current.getBoundingClientRect()
        const coef = dimensions.width / attachment.width
        const width = attachment.width * coef
        const height = attachment.height * coef

        setHeight(height)
        setWidth(width)
        setCoef(coef)
    }, [innerWidth, innerHeight, containerRef])

    return (
        <Modal
            disableBackgroundDismiss
            width="99%"
            height="99%"
            portal={Portal}
            header={
                <>
                    <Heading
                        as="h4"
                        style={{ textTransform: 'capitalize' }}>
                        {type}
                    </Heading>
                    {!!rating && (
                        <View
                            row
                            m="0 0 0 0.5rem"
                            gap="0.5rem">
                            <Icon
                                icon={TbStarFilled}
                                color="var(--f-color-yellow-500)"
                            />
                            <Text fontWeight={700}>{rating}</Text>
                        </View>
                    )}
                    <Flexer />
                    <ModalClose onClick={onDismiss} />
                </>
            }
            onDismiss={onDismiss}
            isVisible={true}>
            <View
                width="calc(100% - 20px)"
                height="calc(100% - 20px)"
                position="absolute"
                radius="var(--f-radius)"
                style={{ inset: 10, overflow: 'auto' }}>
                <View
                    ref={containerRef}
                    position="relative"
                    width="100%"
                    height="100%">
                    <Loading loading={loading} />

                    {comments.map((comment, index) => {
                        return (
                            <ScreenshotComment
                                key={index}
                                annotationIndex={index}
                                index={index}
                                description={comment.description}
                                x={comment.x * coef}
                                y={comment.y * coef}
                            />
                        )
                    })}

                    <div
                        style={{
                            width,
                            height,
                            overflow: 'hidden',
                            backgroundImage: `url(${backgroundImage})`,
                            position: 'relative',
                            borderRadius: 'var(--f-radius)',
                            objectFit: 'contain',
                            zIndex: 30,
                        }}>
                        <canvas
                            ref={canvasRef}
                            height={height}
                            width={width}
                            style={{
                                zIndex: 10,
                                height: height,
                                width: width,
                                position: 'absolute',
                                borderRadius: 'var(--f-radius)',
                                top: 0,
                                left: 0,
                            }}
                        />

                        <svg
                            height={height}
                            width={width}
                            style={{
                                zIndex: 20,
                                width,
                                height,
                                position: 'absolute',
                                borderRadius: 'var(--f-radius)',
                                top: 0,
                                left: 0,
                            }}>
                            {lines.map((line, index) => {
                                const pathData =
                                    'M ' + line.points.map((p) => `${p.x * coef} ${p.y * coef}`).join(' L ')

                                return (
                                    <path
                                        key={index}
                                        d={pathData}
                                        stroke={line.color}
                                        fill="none"
                                        strokeWidth={5 * coef}
                                        strokeLinejoin="round"
                                        strokeLinecap="round"
                                    />
                                )
                            })}

                            {rectangles.map((rectangle, index) => (
                                <g key={index}>
                                    <rect
                                        x={rectangle.x * coef}
                                        y={rectangle.y * coef}
                                        width={rectangle.width * coef}
                                        height={rectangle.height * coef}
                                        stroke={rectangle.stroke}
                                        fill={rectangle.fill}
                                        strokeWidth={5 * coef}
                                        strokeLinejoin="round"
                                        strokeLinecap="round"
                                    />
                                </g>
                            ))}

                            {arrows.map((arrow, index) => (
                                <ScreenshotArrow
                                    key={index}
                                    sx={arrow.sx * coef}
                                    sy={arrow.sy * coef}
                                    ex={arrow.ex * coef}
                                    ey={arrow.ey * coef}
                                    color={arrow.color}
                                    strokeWidth={5 * coef}
                                />
                            ))}
                        </svg>
                    </div>
                </View>
            </View>
        </Modal>
    )
}

const ScreenshotComment = (props: any) => {
    const divRef = useRef(null)
    const [visible, setVisible] = useState(true)
    const [left, setLeft] = useState(0)
    const [top, setTop] = useState(0)
    const popoverClass = useMemo(() => {
        if (left > window.innerWidth / 2) {
            if (top > window.innerHeight / 2) {
                return 'f-popout-bottom-right'
            } else {
                return 'f-popout-top-right'
            }
        }
        if (left <= window.innerWidth / 2) {
            if (top < window.innerHeight / 2) {
                return 'f-popout-bottom-left'
            } else {
                return 'f-popout-top-left'
            }
        }
    }, [left, top])

    const updateCoordinates = () => {
        const { x, y } = props
        const { innerWidth, innerHeight } = window
        let yOffset = 0
        let xOffset = 0

        if (divRef.current) {
            const { height, width } = divRef.current.getBoundingClientRect()
            const containerHeight = height
            const containerWidth = width + 20

            if (y + containerHeight > innerHeight) yOffset = y + containerHeight - innerHeight
            if (x + containerWidth > innerWidth) xOffset = containerWidth + 20
        }

        setLeft(x - xOffset)
        setTop(y - yOffset)
    }

    useEffect(() => {
        updateCoordinates()
    }, [divRef, visible, props.x, props.y])

    return (
        <div
            className="f-row reveal-on-hover"
            onClick={() => setVisible(!visible)}
            style={{
                left: props.x,
                top: props.y,
                padding: '0.5rem 1rem',
                borderRadius: 'var(--f-radius)',
                position: 'absolute',
                zIndex: 40,
                background: 'var(--f-color-accent)',
                cursor: 'pointer',
            }}>
            <span
                className="f-text"
                style={{
                    color: 'var(--f-color-accent-100)',
                    fontWeight: 'bold',
                    zIndex: 1,
                }}>
                #{props.index + 1}
            </span>
            <div
                className={'f-text lg reveal-on-hover-target ' + popoverClass}
                ref={divRef}
                style={{
                    width: 'fit-content',
                    maxWidth: 200,
                    padding: '0.5rem 1rem',
                    borderRadius: 'var(--f-radius)',
                    height: 'fit-content',
                    position: 'absolute',
                    color: 'var(--f-color-accent-100)',
                    background: 'var(--f-color-accent)',
                    zIndex: 2,
                }}>
                {props.description}
            </div>
        </div>
    )
}

const ScreenshotArrow = (props: any) => {
    const [arrow, setArrow] = useState(null)

    useEffect(() => {
        const { sx, sy, ex, ey } = props
        setArrow(
            getArrow(sx, sy, ex, ey, {
                bow: 0,
                stretch: 0.1,
                stretchMin: 0,
                stretchMax: 420,
                padStart: 0,
                padEnd: 0,
                flip: false,
                straights: true,
            })
        )
    }, [props.sx, props.sy, props.ex, props.ey])

    if (!arrow) return null

    const [sx, sy, cx, cy, ex, ey, ae, as, ec] = arrow
    const endAngleAsDegrees = ae * (180 / Math.PI)

    return (
        <g>
            <path
                d={`M${sx},${sy} Q${cx},${cy} ${ex},${ey}`}
                fill="none"
                stroke={props.color}
                strokeWidth={props.strokeWidth}
            />
            <polygon
                style={{ strokeLinejoin: 'round' }}
                stroke={props.color}
                fill={props.color}
                strokeWidth={props.strokeWidth}
                points="0,-6 12,0, 0,6"
                transform={`translate(${ex},${ey}) rotate(${endAngleAsDegrees})`}
            />
        </g>
    )
}
